import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import {
  type ComponentProps,
  type ComponentRef,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { XStack, YStack } from 'tamagui';

import { color, radius, space } from '@/constants/tokens';
import { openURL } from '@/src/utils/linkingUtils';
import PSIcon from './ui/PSIcon';
import PSText from './ui/PSText';

const SUPPORT_EMAIL = 'support@puttingscene.com';
const SUPPORT_PHONE = '+91-9538718754';

/**
 * The two ways to reach us, in a sheet off Settings → Contact us. Render-only:
 * the screen owns `open`, and both rows hand off to the OS (mail client /
 * dialer) and then close — the handoff is the confirmation, so there's nothing
 * to report back.
 */
export default function ContactUsSheet({
  open,
  onClose,
}: {
  open: boolean;
  /** Swipe-down and backdrop-press land here too, so the screen's state stays in sync. */
  onClose: () => void;
}) {
  const { bottom } = useSafeAreaInsets();
  const sheet = useRef<ComponentRef<typeof BottomSheet>>(null);

  // Driven by ref, not by the `index` prop: gorhom resolves a new index to
  // `detents[index]`, and `detents[-1]` is undefined — so `index={-1}` animates
  // the sheet nowhere and it stays open. Only `close()` knows where closed is.
  useEffect(() => {
    if (open) {
      sheet.current?.snapToIndex(0);
    } else {
      sheet.current?.close();
    }
  }, [open]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        pressBehavior="close"
      />
    ),
    []
  );

  const openAndClose = (url: string) => {
    openURL(url);
    onClose();
  };

  return (
    <BottomSheet
      ref={sheet}
      // Mounts closed; the height of its content once open (gorhom's dynamic
      // sizing), so index 0 is the only detent there is.
      index={-1}
      onClose={onClose}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      // Raw token values: none of these three props are Tamagui components.
      backgroundStyle={{
        backgroundColor: color.background,
        borderRadius: radius.base,
      }}
      handleIndicatorStyle={{ backgroundColor: color.borderColor }}
    >
      <BottomSheetView>
        <YStack
          px={'$space.lg'}
          pt={'$space.lg'}
          gap={'$space.lg'}
          // Clear of the home indicator; the sheet has no safe area of its own.
          pb={space.lg + bottom}
        >
          <PSText.H2>Contact</PSText.H2>

          <YStack gap={'$space.sm'}>
            <ContactRow
              icon="mail-outline"
              label="Email us"
              value={SUPPORT_EMAIL}
              onPress={() => openAndClose(`mailto:${SUPPORT_EMAIL}`)}
            />
            <ContactRow
              icon="call-outline"
              label="Call us"
              value={SUPPORT_PHONE}
              // Strip display formatting for the dialer, keep a leading "+";
              // the visible label stays the formatted number.
              onPress={() =>
                openAndClose(`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, '')}`)
              }
            />
          </YStack>
        </YStack>
      </BottomSheetView>
    </BottomSheet>
  );
}

/** One reach-us option: the action as the label, the address/number under it as
 *  the value — the value IS the affordance, so there's no chevron. */
function ContactRow({
  icon,
  label,
  value,
  onPress,
}: {
  icon: ComponentProps<typeof PSIcon>['name'];
  label: string;
  value: string;
  onPress: () => void;
}) {
  return (
    <XStack
      items="center"
      gap="$md"
      px="$lg"
      py="$lg"
      bg="$backgroundSecondary"
      rounded="$base"
      cursor="pointer"
      onPress={onPress}
      pressStyle={{ bg: '$borderColor' }}
    >
      <PSIcon name={icon} color="$textDark" />
      <YStack flex={1} gap="$xs">
        <PSText.Body2 color="$textDark">{label}</PSText.Body2>
        <PSText.Body3 color="$textDarkMuted">{value}</PSText.Body3>
      </YStack>
    </XStack>
  );
}
